import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    AffectedItemsFormGroup,
    BillingAdjustmentDomesticFormGroup,
    CodAttachment,
    CodCreateFormGroup,
    DeliveryErrorDomesticFormGroup,
    DeliveryErrorInternationalFormGroup,
    DocumentationInternationalFormGroup,
    EskerDomesticFormGroup,
    InvoiceErrorDomesticFormGroup,
    InvoiceErrorInternationalFormGroup,
    ItemOrQuantityDomesticFormGroup,
    ItemOrQuantityInternationalFormGroup,
    LargeInstrumentWarehouseDiscrepancyFormGroup,
    OrderEntryDomesticFormGroup,
    OrderEntryInternationalFormGroup,
    ProductDamageDomesticFormGroup,
    ProductDamageInternationalFormGroup,
    ProductDatingDomesticFormGroup,
    ProductDatingInternationalFormGroup,
    ProductTemperatureDomesticFormGroup,
    ProductTemperatureInternationalFormGroup,
    QualityInternationalFormGroup,
    ReferenceNumberDomesticFormGroup,
    ReferenceNumberInternationalFormGroup,
    RegionFormGroup
} from '../models/cod-create.model';
import {
    Region,
    ReferenceNumberTypeDomestic,
    NatureOfComplaintDomestic,
    ReferenceNumberTypeInternational,
    NatureOfComplaintInternational,
    AccountErrorType,
    OrderEntryIssueTypeDomestic,
    FinancialAdjustment,
    OrderEntryIssueTypeInternational,
    ItemOrQuantityIssueTypeDomestic,
    ItemOrQuantityIssueTypeInternational,
    ProductDatingUponReceiptIssueTypeDomestic,
    ProductDatingUponReceiptIssueTypeInternational,
    PackageType,
    PackageTypeCondition,
    DamageIssueType,
    ItemType,
    TemperatureExcursionIdentifier,
    TemperatureExcursionLocation,
    TemperatureIssueType,
    ItemCondition,
    DeliveryIssueTypeInternational,
    DeliveryIssueTypeDomestic,
    AinErrorType,
    DocumentationIssueType,
    DocumentationIssue,
    DocumentationErrorType,
    InvoiceIssueTypeInternational,
    InvoiceIssueTypeDetail,
    InvoiceIssueTypeDomestic,
    BillingAdjustmentIssueType,
    QualityIssueType,
    LabelError,
    EskerOrderIssueType,
    LargeInstrumentWarehouseIssueType,
    MaterialDisposition,
    IOrderLine,
    CountrySpecificLabelIssue
} from '@cod-monorepo/interfaces';
import { CustomValidators } from '../validators/custom-validators';
import { WorkflowValidators } from '../validators/workflow-validators';

export class CodCreateOperations {
    public static initializeForm(): FormGroup<CodCreateFormGroup> {
        return new FormGroup<CodCreateFormGroup>({
            region: new FormGroup<RegionFormGroup>({
                region: new FormControl<Region | null>(null, Validators.required)
            }),
            referenceNumberDomestic: new FormGroup<ReferenceNumberDomesticFormGroup>({
                referenceNumberType: new FormControl<ReferenceNumberTypeDomestic | null>(null, Validators.required),
                referenceNumber: new FormControl<string | null>(null, Validators.required),
                referenceNumberValid: new FormControl<boolean | null>(null, Validators.requiredTrue),
                productDiscrepancy: new FormControl<boolean | null>(null, CustomValidators.requiredFalse()),
                natureOfComplaint: new FormControl<NatureOfComplaintDomestic | null>(null, Validators.required)
            }),
            referenceNumberInternational: new FormGroup<ReferenceNumberInternationalFormGroup>({
                referenceNumberType: new FormControl<ReferenceNumberTypeInternational | null>(
                    null,
                    Validators.required
                ),
                referenceNumber: new FormControl<string | null>(null, Validators.required),
                referenceNumberValid: new FormControl<boolean | null>(null, Validators.requiredTrue),
                productDiscrepancy: new FormControl<boolean | null>(null, CustomValidators.requiredFalse()),
                pgiWithin40Days: new FormControl<boolean | null>(null, Validators.required),
                natureOfComplaint: new FormControl<NatureOfComplaintInternational | null>(null, Validators.required)
            }),
            affectedItems: new FormGroup<AffectedItemsFormGroup>({
                affectedOrderLines: new FormControl<IOrderLine[]>([], CustomValidators.minArrayLength(1)),
                shipToCountryCode: new FormControl<string | null>(null),
                typeValid: new FormControl<boolean | null>(null, Validators.requiredTrue),
                confirmed: new FormControl<boolean>(false, Validators.requiredTrue)
            }),
            orderEntryDomestic: new FormGroup<OrderEntryDomesticFormGroup>(
                {
                    issueType: new FormControl<OrderEntryIssueTypeDomestic | null>(null),
                    accountErrorType: new FormControl<AccountErrorType[] | null>(null),
                    incorrectlyUsed: new FormControl<string | null>(null),
                    correctOrderType: new FormControl<string | null>(null),
                    csrResponsible: new FormControl<string | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.orderEntryDomestic(),
                        WorkflowValidators.required([
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            orderEntryInternational: new FormGroup<OrderEntryInternationalFormGroup>(
                {
                    issueType: new FormControl<OrderEntryIssueTypeInternational | null>(null),
                    errorDescription: new FormControl<string | null>(null),
                    accountErrorType: new FormControl<AccountErrorType | null>(null),
                    canIdentifyCartonId: new FormControl<boolean | null>(null),
                    cartonId: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([])
                },
                {
                    validators: [
                        WorkflowValidators.orderEntryInternational(),
                        WorkflowValidators.required(['financialAdjustment'])
                    ]
                }
            ),
            itemOrQuantityDomestic: new FormGroup<ItemOrQuantityDomesticFormGroup>(
                {
                    issueType: new FormControl<ItemOrQuantityIssueTypeDomestic | null>(null),
                    unorderedItemSmn: new FormControl<string | null>(null),
                    unorderedItemQuantity: new FormControl<number | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.itemQuantityDomestic(),
                        WorkflowValidators.required([
                            'issueType',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            itemOrQuantityInternational: new FormGroup<ItemOrQuantityInternationalFormGroup>(
                {
                    issueType: new FormControl<ItemOrQuantityIssueTypeInternational | null>(null),
                    unorderedItemSmn: new FormControl<string | null>(null),
                    unorderedItemQuantity: new FormControl<number | null>(null),
                    canIdentifyCartonId: new FormControl<boolean | null>(null),
                    cartonId: new FormControl<string | null>(null),
                    lotsDeclinedWithin24Hours: new FormControl<boolean | null>(null),
                    communicationDeclinedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([])
                },
                {
                    validators: [WorkflowValidators.itemQuantityInternational()]
                }
            ),
            productDatingDomestic: new FormGroup<ProductDatingDomesticFormGroup>(
                {
                    issueType: new FormControl<ProductDatingUponReceiptIssueTypeDomestic | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.productDatingDomestic(),
                        WorkflowValidators.required([
                            'issueType',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            productDatingInternational: new FormGroup<ProductDatingInternationalFormGroup>(
                {
                    issueType: new FormControl<ProductDatingUponReceiptIssueTypeInternational | null>(null),
                    declinedWithin24Hours: new FormControl<boolean | null>(null),
                    communicationDeclinedDocuments: new FormControl<CodAttachment[]>([]),
                    batchExpiryMeetsMinShipPolicy: new FormControl<boolean | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([])
                },
                {
                    validators: [
                        WorkflowValidators.productDatingInternational(),
                        WorkflowValidators.required(['declinedWithin24Hours'])
                    ]
                }
            ),
            productDamageDomestic: new FormGroup<ProductDamageDomesticFormGroup>(
                {
                    damageType: new FormControl<DamageIssueType | null>(null),
                    itemType: new FormControl<ItemType | null>(null),
                    documentationNotedIssue: new FormControl<boolean | null>(null),
                    deliveryDocumentation: new FormControl<CodAttachment[]>([]),
                    damageClaimFiled: new FormControl<boolean | null>(null),
                    itemCondition: new FormControl<ItemCondition[]>([]),
                    visualEvidence: new FormControl<CodAttachment[]>([]),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null),
                    packageType: new FormControl<PackageType | null>(null),
                    protectivePackagingUsed: new FormControl<boolean | null>(null),
                    packageTypeCondition: new FormControl<PackageTypeCondition | null>(null),
                    additionalPackagingInfo: new FormControl<string | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.productDamageDomestic(),
                        WorkflowValidators.required([
                            'damageType',
                            'itemType',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment',
                            'itemCondition'
                        ])
                    ]
                }
            ),
            productDamageInternational: new FormGroup<ProductDamageInternationalFormGroup>(
                {
                    damageType: new FormControl<DamageIssueType | null>(null),
                    itemType: new FormControl<ItemType | null>(null),
                    documentationNotedIssue: new FormControl<boolean | null>(null),
                    deliveryDocumentation: new FormControl<CodAttachment[]>([]),
                    explanationOfMissingNote: new FormControl<string | null>(null),
                    damageClaimFiled: new FormControl<boolean | null>(null),
                    itemCondition: new FormControl<ItemCondition[]>([]),
                    visualEvidence: new FormControl<CodAttachment[]>([]),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    packageType: new FormControl<PackageType | null>(null),
                    protectivePackagingUsed: new FormControl<boolean | null>(null),
                    packageTypeCondition: new FormControl<PackageTypeCondition | null>(null),
                    additionalPackagingInfo: new FormControl<string | null>(null),
                    canIdentifyCartonId: new FormControl<boolean | null>(null),
                    cartonId: new FormControl<string | null>(null),
                    carrierTrackingNumber: new FormControl<string | null>(null),
                    damageClaimDocumentation: new FormControl<CodAttachment[]>([])
                },
                {
                    validators: [
                        WorkflowValidators.productDamageInternational(),
                        WorkflowValidators.required(['damageType', 'itemType', 'financialAdjustment', 'itemCondition'])
                    ]
                }
            ),
            productTemperatureDomestic: new FormGroup<ProductTemperatureDomesticFormGroup>(
                {
                    issueType: new FormControl<TemperatureIssueType | null>(null),
                    temperatureExcursionExplanation: new FormControl<string | null>(null),
                    temperatureExcursionEvidence: new FormControl<CodAttachment[]>([]),
                    itemCondition: new FormControl<ItemCondition[]>([]),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.productTemperatureDomestic(),
                        WorkflowValidators.required([
                            'issueType',
                            'temperatureExcursionExplanation',
                            'temperatureExcursionEvidence',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment',
                            'itemCondition'
                        ])
                    ]
                }
            ),
            productTemperatureInternational: new FormGroup<ProductTemperatureInternationalFormGroup>(
                {
                    issueType: new FormControl<TemperatureIssueType | null>(null),
                    dateOfPotentialTemperatureExcursion: new FormControl<string | null>(null),
                    locationOfPotentialTemperatureExcursion: new FormControl<TemperatureExcursionLocation | null>(null),
                    exactLocation: new FormControl<string | null>(null),
                    temperatureExcursionIdentifiedBy: new FormControl<TemperatureExcursionIdentifier | null>(null),
                    includesShippingTemperatureRequirements: new FormControl<boolean | null>(null),
                    documentationNotedIssue: new FormControl<boolean | null>(null),
                    deliveryDocumentation: new FormControl<CodAttachment[]>([]),
                    explanationOfMissingNote: new FormControl<string | null>(null),
                    claimFiled: new FormControl<boolean | null>(null),
                    carrierClaimDocumentation: new FormControl<CodAttachment[]>([]),
                    explainWhyTemperatureExcursionSuspected: new FormControl<string | null>(null),
                    temperatureExcursionEvidence: new FormControl<CodAttachment[]>([]),
                    itemCondition: new FormControl<ItemCondition[]>([]),
                    carrierTrackingNumber: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([])
                },
                {
                    validators: [
                        WorkflowValidators.productTemperatureInternational(),
                        WorkflowValidators.required([
                            'issueType',
                            'dateOfPotentialTemperatureExcursion',
                            'locationOfPotentialTemperatureExcursion',
                            'exactLocation',
                            'temperatureExcursionIdentifiedBy',
                            'includesShippingTemperatureRequirements',
                            'temperatureExcursionEvidence',
                            'carrierTrackingNumber',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            deliveryErrorDomestic: new FormGroup<DeliveryErrorDomesticFormGroup>(
                {
                    issueType: new FormControl<DeliveryIssueTypeDomestic | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null),
                    deliveryDocumentationMatchesAddress: new FormControl<boolean | null>(null),
                    addressShippedTo: new FormControl<string | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.deliveryErrorDomestic(),
                        WorkflowValidators.required([
                            'issueType',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            deliveryErrorInternational: new FormGroup<DeliveryErrorInternationalFormGroup>(
                {
                    issueType: new FormControl<DeliveryIssueTypeInternational | null>(null),
                    shipmentNumber: new FormControl<string | null>(null),
                    deliveryDocumentation: new FormControl<CodAttachment[] | null>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    supportDocumentation: new FormControl<CodAttachment[] | null>([]),
                    deliveryDocumentationMatchesAddress: new FormControl<boolean | null>(null),
                    addressShippedTo: new FormControl<string | null>(null),
                    deliveryDocumentationMatchesPort: new FormControl<boolean | null>(null),
                    portShippedTo: new FormControl<string | null>(null),
                    carrier: new FormControl<string | null>(null),
                    ainErrorType: new FormControl<AinErrorType | null>(null),
                    replacementOrderRequired: new FormControl<boolean | null>(null),
                    replacementOrderNumber: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([])
                },
                {
                    validators: [
                        WorkflowValidators.deliveryErrorInternational(),
                        WorkflowValidators.required(['issueType', 'financialAdjustment', 'additionalInfo'])
                    ]
                }
            ),
            documentationInternational: new FormGroup<DocumentationInternationalFormGroup>(
                {
                    issueType: new FormControl<DocumentationIssueType | null>(null),
                    documentationIssue: new FormControl<DocumentationIssue | null>(null),
                    documentationIssueOther: new FormControl<string | null>(null),
                    specialInstructions: new FormControl<boolean | null>(null),
                    documentationErrorType: new FormControl<DocumentationErrorType | null>(null),
                    documentationErrorTypeOther: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[] | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.documentationInternational(),
                        WorkflowValidators.required([
                            'issueType',
                            'documentationIssue',
                            'specialInstructions',
                            'documentationErrorType',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            invoiceErrorDomestic: new FormGroup<InvoiceErrorDomesticFormGroup>(
                {
                    invoiceErrorType: new FormControl<InvoiceIssueTypeDomestic | null>(null),
                    invoiceErrorDetailType: new FormControl<InvoiceIssueTypeDetail | null>(null),
                    instructionsContained: new FormControl<boolean | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.invoiceErrorDomestic(),
                        WorkflowValidators.required([
                            'invoiceErrorType',
                            'invoiceErrorDetailType',
                            'instructionsContained',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            invoiceErrorInternational: new FormGroup<InvoiceErrorInternationalFormGroup>(
                {
                    invoiceErrorType: new FormControl<InvoiceIssueTypeInternational | null>(null),
                    invoiceErrorTypeOtherInfo: new FormControl<string | null>(null),
                    errorsIdentifiedWithin24hours: new FormControl<boolean | null>(null),
                    communicationDocuments: new FormControl<CodAttachment[]>([]),
                    invoiceMatchesIODC: new FormControl<boolean | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    additionalInfo: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[]>([])
                },
                {
                    validators: [
                        WorkflowValidators.invoiceErrorInternational(),
                        WorkflowValidators.required([
                            'invoiceErrorType',
                            'errorsIdentifiedWithin24hours',
                            'financialAdjustment',
                            'additionalInfo'
                        ])
                    ]
                }
            ),
            billingAdjustmentDomestic: new FormGroup<BillingAdjustmentDomesticFormGroup>(
                {
                    issueType: new FormControl<BillingAdjustmentIssueType | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[] | null>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.billingAdjustmentDomestic(),
                        WorkflowValidators.required([
                            'issueType',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            qualityInternational: new FormGroup<QualityInternationalFormGroup>(
                {
                    issueType: new FormControl<QualityIssueType | null>(null),
                    countrySpecificLabelMissing: new FormControl<CountrySpecificLabelIssue | null>(null),
                    labelError: new FormControl<LabelError | null>(null),
                    materialDisposition: new FormControl<MaterialDisposition | null>(null),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<FinancialAdjustment | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[] | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.qualityInternational(),
                        WorkflowValidators.required([
                            'issueType',
                            'materialDisposition',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            eskerDiscrepancyDomestic: new FormGroup<EskerDomesticFormGroup>(
                {
                    issueType: new FormControl<EskerOrderIssueType | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[] | null>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.eskerDiscrepancyDomestic(),
                        WorkflowValidators.required([
                            'issueType',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            ),
            largeInstrumentWarehouseDiscrepancy: new FormGroup<LargeInstrumentWarehouseDiscrepancyFormGroup>(
                {
                    issueType: new FormControl<LargeInstrumentWarehouseIssueType | null>(null),
                    documentationNotedIssue: new FormControl<boolean | null>(null),
                    deliveryDocumentation: new FormControl<CodAttachment[] | null>([]),
                    explanationOfMissingNote: new FormControl<string | null>(null),
                    claimFiled: new FormControl<boolean | null>(null),
                    carrierClaimDocumentation: new FormControl<CodAttachment[] | null>([]),
                    itemCondition: new FormControl<ItemCondition[] | null>(null),
                    additionalPackagingInfo: new FormControl<string | null>(null),
                    visualEvidence: new FormControl<CodAttachment[] | null>([]),
                    packageType: new FormControl<PackageType | null>(null),
                    packageTypeCondition: new FormControl<PackageTypeCondition | null>(null),
                    creditNumber: new FormControl<string | null>(null),
                    callTagTrackingNumber: new FormControl<string | null>(null),
                    relatedDocuments: new FormControl<CodAttachment[] | null>([]),
                    additionalInfo: new FormControl<string | null>(null),
                    financialAdjustment: new FormControl<boolean | null>(null),
                    filedWithin30Days: new FormControl<boolean | null>(null),
                    managerApprovedRequest: new FormControl<string | null>(null),
                    adjustmentAmount: new FormControl<number | null>(null)
                },
                {
                    validators: [
                        WorkflowValidators.largeInstrumentWarehouseDiscrepancy(),
                        WorkflowValidators.required([
                            'issueType',
                            'creditNumber',
                            'callTagTrackingNumber',
                            'additionalInfo',
                            'financialAdjustment'
                        ])
                    ]
                }
            )
        });
    }
}
